/**
 * <product-countdown> — 通用倒计时 Web Component
 *
 * 特点：
 *  - 每个实例自带作用域，同一页面可放任意多个，互不干扰。
 *  - 全站只用一个共享定时器驱动所有实例（不会每个倒计时各开一个 setInterval）。
 *  - 自动支持动态插入的元素（quick-shop 弹窗、无限滚动等）。
 *  - 日期为空 / 非法时自动隐藏，不会在控制台报错。
 *
 * 用法：
 *   <product-countdown data-end="2025-12-31T23:59:59">
 *     <span data-cd="days">00</span>
 *     <span data-cd="hours">00</span>
 *     <span data-cd="minutes">00</span>
 *     <span data-cd="seconds">00</span>
 *   </product-countdown>
 *
 * 可选属性：
 *   data-end         结束时间（ISO 字符串、"2025-12-31"、"2025/12/31 23:59" 等）
 *   data-keep-on-end 过期后保留显示 00:00:00（默认过期即隐藏）
 *
 * 移植到其它站点：拷贝 product-countdown.js + product-countdown.css，
 * 在 layout/theme.liquid 的 <head> 各引入一行，再输出上面的标记即可。
 */
(function () {
  'use strict';

  // 避免重复定义（脚本被多次引入也安全）
  if (window.customElements && customElements.get('product-countdown')) return;

  var SECOND = 1000;
  var MINUTE = SECOND * 60;
  var HOUR = MINUTE * 60;
  var DAY = HOUR * 24;

  // 所有存活实例共享一个定时器
  var instances = new Set();
  var ticker = null;
  var aligning = false;

  function tickAll() {
    var now = Date.now();
    instances.forEach(function (el) {
      el.refresh(now);
    });
  }

  // 把刷新对齐到墙上整秒（xx:xx:xx.000），这样和官方
  // countdown-timer（结束秒固定为 :00）在同一时刻翻秒，不再差 1 秒。
  function startTicker() {
    if (ticker !== null || aligning) return;
    aligning = true;
    var delay = SECOND - (Date.now() % SECOND);
    setTimeout(function () {
      aligning = false;
      if (instances.size === 0) return;
      tickAll();
      ticker = setInterval(tickAll, SECOND);
    }, delay);
  }

  function stopTickerIfIdle() {
    if (ticker !== null && instances.size === 0) {
      clearInterval(ticker);
      ticker = null;
    }
  }

  function pad(n) {
    return n < 10 ? '0' + n : '' + n;
  }

  // 把多种日期写法解析成时间戳，解析失败返回 null
  function parseDate(raw) {
    if (!raw) return null;
    raw = String(raw).trim();
    if (!raw) return null;
    var t = new Date(raw).getTime();
    if (isNaN(t)) {
      // Safari 对 "2025-01-01 03:00" 这类写法挑剔，换成斜杠再试
      t = new Date(raw.replace(/-/g, '/')).getTime();
    }
    return isNaN(t) ? null : t;
  }

  function ProductCountdown() {
    return Reflect.construct(HTMLElement, [], ProductCountdown);
  }
  ProductCountdown.prototype = Object.create(HTMLElement.prototype);
  ProductCountdown.prototype.constructor = ProductCountdown;

  ProductCountdown.prototype.connectedCallback = function () {
    this.end = parseDate(this.getAttribute('data-end'));

    // 没有有效结束时间：隐藏，避免显示 NaN / 报错
    if (this.end === null) {
      this.style.display = 'none';
      return;
    }

    this.cells = {
      days: this.querySelector('[data-cd="days"]'),
      hours: this.querySelector('[data-cd="hours"]'),
      minutes: this.querySelector('[data-cd="minutes"]'),
      seconds: this.querySelector('[data-cd="seconds"]')
    };
    this.keepOnEnd = this.hasAttribute('data-keep-on-end');

    this.refresh(Date.now());
    if (!this.expired) {
      instances.add(this);
      startTicker();
    }
  };

  ProductCountdown.prototype.disconnectedCallback = function () {
    instances.delete(this);
    stopTickerIfIdle();
  };

  ProductCountdown.prototype.write = function (key, value) {
    var cell = this.cells[key];
    if (cell) cell.textContent = pad(value);
  };

  ProductCountdown.prototype.refresh = function (now) {
    var distance = this.end - now;

    if (distance <= 0) {
      this.write('days', 0);
      this.write('hours', 0);
      this.write('minutes', 0);
      this.write('seconds', 0);
      this.expired = true;
      instances.delete(this);
      stopTickerIfIdle();
      this.classList.add('is-expired');
      if (!this.keepOnEnd) this.style.display = 'none';
      this.dispatchEvent(new CustomEvent('countdown:end', { bubbles: true }));
      return;
    }

    this.write('days', Math.floor(distance / DAY));
    this.write('hours', Math.floor((distance % DAY) / HOUR));
    this.write('minutes', Math.floor((distance % HOUR) / MINUTE));
    this.write('seconds', Math.floor((distance % MINUTE) / SECOND));
  };

  customElements.define('product-countdown', ProductCountdown);
})();